import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import { useGoogleAuth } from '@questlabs/auth-js';
import SafeIcon from '../../common/SafeIcon';
import {
  createWalkInCheckIn,
  loadEventCheckIns,
  updateCheckInStatus,
} from './eventAttendanceApi';

const { FiCheck, FiRefreshCw, FiSearch, FiUsers } = FiIcons;

const EventAttendanceCheckIn = ({ event, memorial }) => {
  const { user, initialized, loading: authLoading } = useGoogleAuth();
  const [guests, setGuests] = useState([]);
  const [query, setQuery] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [partySize, setPartySize] = useState('1');
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!user || !event?.id) return;
    setLoading(true);
    setError('');
    const response = await loadEventCheckIns(event.id);
    if (response?.status === 'success') {
      setGuests(response?.data ?? []);
    } else {
      setError(response?.error || 'Could not load attendance records.');
    }
    setLoading(false);
  }, [event?.id, user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredGuests = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return guests;
    return guests.filter(guest =>
      (guest?.guest_name ?? '').toLowerCase().includes(term) ||
      (guest?.guest_email ?? '').toLowerCase().includes(term)
    );
  }, [guests, query]);

  const checkedInCount = guests.filter(guest => Number(guest?.checked_in ?? 0) === 1).length;

  const handleAddWalkIn = async (eventSubmit) => {
    eventSubmit.preventDefault();
    if (!guestName.trim()) return;
    setSaving(true);
    setError('');
    const response = await createWalkInCheckIn({
      event,
      memorial,
      guestName,
      guestEmail,
      partySize: Math.max(1, Number.parseInt(partySize, 10) || 1),
    });
    if (response?.status === 'success') {
      setGuestName('');
      setGuestEmail('');
      setPartySize('1');
      await refresh();
    } else {
      setError(response?.error || 'Could not add the walk-in.');
    }
    setSaving(false);
  };

  const handleToggle = async (guest) => {
    const checkedIn = Number(guest?.checked_in ?? 0) === 1;
    setBusyId(guest?.id ?? null);
    setError('');
    const response = await updateCheckInStatus(guest, !checkedIn);
    if (response?.status === 'success') {
      await refresh();
    } else {
      setError(response?.error || 'Could not update this check-in.');
    }
    setBusyId(null);
  };

  return (
    <section className="mt-6 rounded-3xl border border-brand-dark bg-white p-5 md:p-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-light text-accent-gold">
            <SafeIcon icon={FiUsers} className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-serif text-lg text-accent-navy">Attendance Check-In</h4>
            <p className="text-xs text-text-muted">
              {checkedInCount} checked in · {guests.length} guests
            </p>
          </div>
        </div>
        <button type="button" onClick={refresh} disabled={loading || !user}
          className="flex items-center gap-2 rounded-xl border border-brand-dark px-3 py-2 text-[10px] font-black uppercase tracking-widest text-accent-navy hover:border-accent-gold disabled:opacity-50">
          <SafeIcon icon={FiRefreshCw} className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {authLoading || !initialized ? (
        <p className="mt-5 text-sm text-text-muted">Checking sign-in…</p>
      ) : !user ? (
        <p className="mt-5 rounded-2xl bg-brand-light px-4 py-3 text-sm text-text-muted">
          Sign in with Google to manage this event’s attendance.
        </p>
      ) : (
        <>
          <form onSubmit={handleAddWalkIn} className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_100px_auto]">
            <input required value={guestName} onChange={e => setGuestName(e.target.value)}
              aria-label="Guest name" placeholder="Guest name"
              className="rounded-xl border border-brand-dark bg-brand-light px-4 py-3 text-sm outline-none focus:border-accent-gold" />
            <input type="email" value={guestEmail} onChange={e => setGuestEmail(e.target.value)}
              aria-label="Guest email" placeholder="Email (optional)"
              className="rounded-xl border border-brand-dark bg-brand-light px-4 py-3 text-sm outline-none focus:border-accent-gold" />
            <input type="number" min="1" value={partySize} onChange={e => setPartySize(e.target.value)}
              aria-label="Party size"
              className="rounded-xl border border-brand-dark bg-brand-light px-4 py-3 text-sm outline-none focus:border-accent-gold" />
            <button type="submit" disabled={saving}
              className="flex items-center justify-center gap-2 rounded-xl bg-accent-navy px-4 py-3 text-[10px] font-black uppercase tracking-widest text-white hover:bg-accent-gold disabled:opacity-50">
              <SafeIcon icon={FiCheck} className="h-4 w-4" />
              {saving ? 'Adding…' : 'Check In Walk-In'}
            </button>
          </form>

          <div className="relative mt-5">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <input value={query} onChange={e => setQuery(e.target.value)}
              aria-label="Search guests" placeholder="Search guest list"
              className="w-full rounded-xl border border-brand-dark py-3 pl-10 pr-4 text-sm outline-none focus:border-accent-gold" />
          </div>

          {error && <p role="alert" className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
          {loading ? (
            <p className="mt-5 text-sm text-text-muted">Loading attendance…</p>
          ) : filteredGuests.length ? (
            <div className="mt-4 divide-y divide-brand-light">
              {filteredGuests.map((guest, index) => {
                const arrived = Number(guest?.checked_in ?? 0) === 1;
                return (
                  <div key={guest?.id ?? `${guest?.guest_email ?? guest?.guest_name}-${index}`}
                    className="flex flex-wrap items-center justify-between gap-3 py-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-accent-navy">{guest?.guest_name ?? 'Guest'}</p>
                      <p className="text-xs text-text-muted">
                        {guest?.guest_email ?? 'No email'} · Party of {guest?.party_size ?? 1}
                      </p>
                    </div>
                    <button type="button" onClick={() => handleToggle(guest)}
                      disabled={busyId === guest?.id || guest?.id == null}
                      className={`rounded-xl px-4 py-2 text-[10px] font-black uppercase tracking-widest disabled:opacity-50 ${arrived ? 'bg-green-50 text-green-700' : 'bg-brand-light text-accent-navy hover:bg-accent-gold'}`}>
                      {busyId === guest?.id ? 'Saving…' : arrived ? 'Checked In' : 'Check In'}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="mt-5 rounded-2xl border border-dashed border-brand-dark px-4 py-6 text-center text-sm text-text-muted">
              {query ? 'No guests match that search.' : 'No guests are on this event roster yet. Add walk-ins as they arrive.'}
            </p>
          )}
        </>
      )}
    </section>
  );
};

export default EventAttendanceCheckIn;